import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import "./index.css";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { setBackendDown } from "./lib/backendStatus";
import BackendDownOverlay from "./components/BackendDownOverlay";
import { IncidentCrashBar } from "./components/IncidentReporter";
import { installErrorCollector, recordApiCall, isHelpdeskApiUrl } from "./utils/errorCollector";
import { patchChromeTranslateDom } from "./utils/patchChromeTranslate";
import {
  TA_REPORTS_ROUTE_PERMS,
  TA_SETUP_ROUTE_PERMS,
} from "./utils/taAdminAccess";
import RLoaderScreen from "./components/RLoaderScreen";
import { lazyPage } from "./utils/lazyPage";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import UserDashboard from "./pages/UserDashboard";
import LandingRedirect from "./components/LandingRedirect";
// TRIAGE-UNIFY: Unified Command Center
// MKTRESP-1: Marketing Responses Module
// RD-RESPONSES-1: R&D Response Workflow
// FEEDBACK-1: Team Feedback System
// INTEL-V2-PRODUCTS: parallel V2 pipeline surface
import IntelV2Layout from "./components/IntelV2Layout";
import MarketingResponsesLayout from "./components/MarketingResponsesLayout";
import IntelStreamLayout from "./components/IntelStreamLayout";
import LeadTriageLayout from "./components/LeadTriageLayout";
// VOICE-2: Journal & To-Do pages

// CI-IDEAS-1: CI Ideas Backlog

// WORKFLOW-1: Workflow Module

// REVENUE-1 + REVENUE-2: Revenue Forecasting Module
// RevenueDashboard removed — superseded by OpsDashboard (OPS-ROUTES)
// REVENUE-3: Invoicing & AR

// OPS-1: Operations Module

// OPS-1B: Operations Module Phase 2

// ISO Module (F0-2) — placeholder until D1/D2/D3/D9 ship real pages
// ISO D8-BIBLIA — Control Center incident placeholders (RQ-008 + RQ-009)
// ISO D1 — Document Matrix (RQ-001.3 + RQ-001.4)
// ISO D2 — Non-Conformities (RQ-007.2 + RQ-007.3)
// ISO O4+D6 — Customer Satisfaction (RQ-004.*)
// ISO D3 — Action Plans (RQ-010.3 + RQ-010.4 + RQ-010.6)
// ISO D4 — Search Center (RQ-002.1 - RQ-002.4)
// ISO D5 — Coding Wizard (RQ-003.1 - RQ-003.3)
// ISO D11 — Document Requests (RQ-013 + N10)
// import DocumentRequests from "./pages/iso/DocumentRequests"; // page no longer used
// ISO D14 — Document Approval Workflow UI (RQ-016 + N13)
// ISO Admin Panel — Laura/ISO admin view + request-to-document linkage (RQ-017)
// ISO Risk & Access Control — Admin-only risk overview + iso.* permission matrix

// RRO-CPM-1: Continuous Performance Management
// CpmMyRro removed — dead import, no route, backend endpoint missing


// Zoho People → ROS UI mockup (People module)

// TIMESHEET-1: DCAA-Compliant Timekeeping
import TaAdminShell from "./components/TaAdminShell";

// PERSONNEL-1: Bulk Timesheet Import

// TIMESHEET-1A: PTO Enhancement Patch

// CORPUS-1: Reference Corpus

// INTAKE-D: Inbound API for External Source Integrations

// STAFFING-1: Key Talent Pipeline
// STAFFING-3: Staffing Admin Config

// INTEL-TIER1-PHASE1B: Agency Intelligence Dashboard

// INTEL-TIER1-PHASE3: Morning Brief Reorganization & Constellation Graph
// MorningBriefPage (V1 /intel/brief) retired in the Morning Brief consolidation — /intel/brief
// now redirects to /intel/brief-v2 (Roster V2). MorningBriefPage.jsx kept on disk (soft-retire).
// BRIEF-V2: Standup-friendly daily intel brief with admin-managed entity rosters — now the brief
// INTEL-V2: Morning Brief V2 cards page (v1.55) → Exec CO-brief (v1.75) → retired in the
// Morning Brief consolidation. /intel/morning-brief-v2 now redirects to /intel/brief-v2.
// IntelMorningBriefExec.jsx is kept on disk (soft-retire) but no longer routed.
// INTEL-V2-PRODUCTS: Deep View for a Morning Brief V2 card (full per-analysis detail with score breakdown)
// INTEL-V2-PRODUCTS: V2 Frontier Brief (compile-on-demand, V2 L2-analysis input, Opus synthesis)
// IntelV2FrontierBrief retired in the Daily Frontier AI consolidation —
// /intel/frontier-v2 redirects to /intel/daily-frontier-ai. Page file
// kept on disk (soft-retire).
// INTEL-V2-PRODUCTS: V2 Daily AI Report (5-section Bloomberg-style synthesis from L2 input, Opus)
// INTEL-V2-PRODUCTS: V2 Weekly Report (6-section recap with thread evolution + conflict surfacing)
// INTEL-V2-PRODUCTS: Phase 1 quality audit page (sample + rate L1 / L2 outputs)
// INTEL-V2-PRODUCTS: Cross-kind archive of completed V2 long-form briefs

// INTEL-SPEAKERS-FE: Intel Speakers, Events, Videos pages
// INTEL-2-PATCH: Curated Feeds + M&A Radar
// DISCOVERY-1: Discovery Scout Agent
// INTEL-CMD: Intelligence Command Center
// IntelFrontierBrief (V1) retired in the Daily Frontier AI consolidation —
// /intel/frontier + /intel/frontier/:briefId redirect to
// /intel/daily-frontier-ai. Page file kept on disk (soft-retire).
// INTEL-REBUILD-S8: New pages

// CONTENT-FE-1: Content Engine pages

// Addendum 2: Builder pages

// Legacy redirect: /opportunity/:id -> /leads/lead/:id
// Pages are loaded the first time they are visited, not in the main bundle.
// New pages go here too: `const MyPage = lazyPage(() => import("./pages/MyPage"));`
const Dashboard = lazyPage(() => import("./pages/Dashboard"));
const OpportunityDetail = lazyPage(() => import("./pages/OpportunityDetail"));
const Opportunities = lazyPage(() => import("./pages/Opportunities"));
const Upload = lazyPage(() => import("./pages/Upload"));
const Settings = lazyPage(() => import("./pages/Settings"));
const SamSearch = lazyPage(() => import("./pages/SamSearch"));
const Help = lazyPage(() => import("./pages/Help"));
const ActivityLog = lazyPage(() => import("./pages/ActivityLog"));
const UsageBoard = lazyPage(() => import("./pages/UsageBoard"));
const About = lazyPage(() => import("./pages/About"));
const DataAdmin = lazyPage(() => import("./pages/DataAdmin"));
const OperationsDashboard = lazyPage(() => import("./pages/OperationsDashboard"));
const TaskMonitor = lazyPage(() => import("./pages/TaskMonitor"));
const FocusCompliance = lazyPage(() => import("./pages/FocusCompliance"));
const SystemLog = lazyPage(() => import("./pages/SystemLog"));
const ProactiveLeads = lazyPage(() => import("./pages/ProactiveLeads"));
const ProactiveLeadDetail = lazyPage(() => import("./pages/ProactiveLeadDetail"));
const Users = lazyPage(() => import("./pages/Users"));
const CrmPipeline = lazyPage(() => import("./pages/CrmPipeline"));
const CrmOpportunities = lazyPage(() => import("./pages/CrmOpportunities"));
const CrmOpportunityDetail = lazyPage(() => import("./pages/CrmOpportunityDetail"));
const CrmContacts = lazyPage(() => import("./pages/CrmContacts"));
const CrmExceptions = lazyPage(() => import("./pages/CrmExceptions"));
const CrmContactDetail = lazyPage(() => import("./pages/CrmContactDetail"));
const CrmOrganizations = lazyPage(() => import("./pages/CrmOrganizations"));
const CrmOrganizationDetail = lazyPage(() => import("./pages/CrmOrganizationDetail"));
const CrmTasks = lazyPage(() => import("./pages/CrmTasks"));
const CrmImport = lazyPage(() => import("./pages/CrmImport"));
const CrmAttentionQueue = lazyPage(() => import("./pages/CrmAttentionQueue"));
const UnifiedPipeline = lazyPage(() => import("./pages/UnifiedPipeline"));
const BulkIngestionResults = lazyPage(() => import("./pages/BulkIngestionResults"));
const SamBulkIngestion = lazyPage(() => import("./pages/SamBulkIngestion"));
const HigherGovBulkIngestion = lazyPage(() => import("./pages/HigherGovBulkIngestion"));
const LakeStream = lazyPage(() => import("./pages/LakeStream"));
const LakeBrief = lazyPage(() => import("./pages/LakeBrief"));
const LakeSearch = lazyPage(() => import("./pages/LakeSearch"));
const LakeAdmin = lazyPage(() => import("./pages/LakeAdmin"));
const IntelDashboard = lazyPage(() => import("./pages/IntelDashboard"));
const LakeActions = lazyPage(() => import("./pages/LakeActions"));
const CoordMyTasks = lazyPage(() => import("./pages/CoordMyTasks"));
const UnsolicitedOutreachBoard = lazyPage(() => import("./pages/UnsolicitedOutreachBoard"));
const CoordImpressions = lazyPage(() => import("./pages/CoordImpressions"));
const CoordAccountability = lazyPage(() => import("./pages/CoordAccountability"));
const UserProfile = lazyPage(() => import("./pages/UserProfile"));
const RolesAdmin = lazyPage(() => import("./pages/RolesAdmin"));
const PlatformControl = lazyPage(() => import("./pages/PlatformControl"));
const PromptEvolution = lazyPage(() => import("./pages/PromptEvolution"));
const ForecastDashboard = lazyPage(() => import("./pages/ForecastDashboard"));
const TriageCenter = lazyPage(() => import("./pages/TriageCenter"));
const AttachmentsAudit = lazyPage(() => import("./pages/AttachmentsAudit"));
const LeadTrustAudit = lazyPage(() => import("./pages/LeadTrustAudit"));
const PastPerformance = lazyPage(() => import("./pages/PastPerformance"));
const PastPerformanceDetail = lazyPage(() => import("./pages/PastPerformanceDetail"));
const MarketingResponsesBoard = lazyPage(() => import("./pages/MarketingResponsesBoard"));
const MarketingResponseDetail = lazyPage(() => import("./pages/MarketingResponseDetail"));
const MarketingResponsesStats = lazyPage(() => import("./pages/MarketingResponsesStats"));
const MarketingResponsesRoutingAdmin = lazyPage(() => import("./pages/MarketingResponsesRoutingAdmin"));
const RdResponseBoard = lazyPage(() => import("./pages/RdResponseBoard"));
const RdResponseDetail = lazyPage(() => import("./pages/RdResponseDetail"));
const RdResponseEnroll = lazyPage(() => import("./pages/RdResponseEnroll"));
const MyFeedbackSubmissions = lazyPage(() => import("./pages/MyFeedbackSubmissions"));
const FeedbackDetail = lazyPage(() => import("./pages/FeedbackDetail"));
const FeedbackAdmin = lazyPage(() => import("./pages/FeedbackAdmin"));
const Helpdesk = lazyPage(() => import("./pages/Helpdesk"));
const HelpdeskDetail = lazyPage(() => import("./pages/HelpdeskDetail"));
const NotificationSettings = lazyPage(() => import("./pages/NotificationSettings"));
const ServicesAdmin = lazyPage(() => import("./pages/ServicesAdmin"));
const IntelV2Feed = lazyPage(() => import("./pages/IntelV2Feed"));
const IntelV2Implications = lazyPage(() => import("./pages/IntelV2Implications"));
const IntelV2Decisions = lazyPage(() => import("./pages/IntelV2Decisions"));
const IntelV2BreakingAlerts = lazyPage(() => import("./pages/IntelV2BreakingAlerts"));
const IntelV2Assessments = lazyPage(() => import("./pages/IntelV2Assessments"));
const IntelV2Fusion = lazyPage(() => import("./pages/IntelV2Fusion"));
const PersonnelList = lazyPage(() => import("./pages/PersonnelList"));
const PersonnelCertifications = lazyPage(() => import("./pages/PersonnelCertifications"));
const RROView = lazyPage(() => import("./pages/RROView"));
const ActionablesView = lazyPage(() => import("./pages/ActionablesView"));
const AARList = lazyPage(() => import("./pages/AARList"));
const AARDetail = lazyPage(() => import("./pages/AARDetail"));
const Journal = lazyPage(() => import("./pages/Journal"));
const MyTodos = lazyPage(() => import("./pages/MyTodos"));
const PeopleNotepad = lazyPage(() => import("./pages/PeopleNotepad"));
const HubPage = lazyPage(() => import("./pages/HubPage"));
const TranscriptTool = lazyPage(() => import("./pages/TranscriptTool"));
const HubTemplates = lazyPage(() => import("./pages/HubTemplates"));
const HubDocuments = lazyPage(() => import("./pages/HubDocuments"));
const HubTeamNotes = lazyPage(() => import("./pages/HubTeamNotes"));
const AgentRegistry = lazyPage(() => import("./pages/AgentRegistry"));
const AgentDetail = lazyPage(() => import("./pages/AgentDetail"));
const AgentRuns = lazyPage(() => import("./pages/AgentRuns"));
const AgentBuilder = lazyPage(() => import("./pages/AgentBuilder"));
const AgentTemplates = lazyPage(() => import("./pages/AgentTemplates"));
const PendingApproval = lazyPage(() => import("./pages/PendingApproval"));
const ContractVehicles = lazyPage(() => import("./pages/ContractVehicles"));
const ContractVehicleDetail = lazyPage(() => import("./pages/ContractVehicleDetail"));
const CIIdeasBacklog = lazyPage(() => import("./pages/CIIdeasBacklog"));
const WorkflowMyTasks = lazyPage(() => import("./pages/WorkflowMyTasks"));
const WorkflowTemplates = lazyPage(() => import("./pages/WorkflowTemplates"));
const WorkflowTemplateDetail = lazyPage(() => import("./pages/WorkflowTemplateDetail"));
const WorkflowRuns = lazyPage(() => import("./pages/WorkflowRuns"));
const WorkflowRunDetail = lazyPage(() => import("./pages/WorkflowRunDetail"));
const WorkflowSchedules = lazyPage(() => import("./pages/WorkflowSchedules"));
const WorkflowReports = lazyPage(() => import("./pages/WorkflowReports"));
const WorkflowAgentPerformance = lazyPage(() => import("./pages/WorkflowAgentPerformance"));
const RevenueProjects = lazyPage(() => import("./pages/RevenueProjects"));
const RevenueProjectDetail = lazyPage(() => import("./pages/RevenueProjectDetail"));
const RevenueForecast = lazyPage(() => import("./pages/RevenueForecast"));
const RevenueAllocation = lazyPage(() => import("./pages/RevenueAllocation"));
const RevenueSchedule = lazyPage(() => import("./pages/RevenueSchedule"));
const RevenueInvoices = lazyPage(() => import("./pages/RevenueInvoices"));
const RevenueInvoiceDetail = lazyPage(() => import("./pages/RevenueInvoiceDetail"));
const WorkforcePlanning = lazyPage(() => import("./pages/WorkforcePlanning"));
const OpsProfitability = lazyPage(() => import("./pages/OpsProfitability"));
const OpsDashboard = lazyPage(() => import("./pages/OpsDashboard"));
const OpsProjectDetail = lazyPage(() => import("./pages/OpsProjectDetail"));
const IsoHome = lazyPage(() => import("./pages/iso/IsoHome"));
const ClausesAdmin = lazyPage(() => import("./pages/iso/ClausesAdmin"));
const ControlCenter = lazyPage(() => import("./pages/iso/ControlCenter"));
const ObjectivesKpis = lazyPage(() => import("./pages/iso/ObjectivesKpis"));
const DocumentMatrix = lazyPage(() => import("./pages/iso/DocumentMatrix"));
const NonConformities = lazyPage(() => import("./pages/iso/NonConformities"));
const RiskMatrix = lazyPage(() => import("./pages/iso/RiskMatrix"));
const Satisfaction = lazyPage(() => import("./pages/iso/Satisfaction"));
const ActionPlans = lazyPage(() => import("./pages/iso/ActionPlans"));
const SearchCenter = lazyPage(() => import("./pages/iso/SearchCenter"));
const CodingWizard = lazyPage(() => import("./pages/iso/CodingWizard"));
const IsoAdminPanel = lazyPage(() => import("./pages/iso/IsoAdminPanel"));
const MyPanelIso = lazyPage(() => import("./pages/iso/MyPanelIso"));
const IsoRiskAccessControl = lazyPage(() => import("./pages/iso/IsoRiskAccessControl"));
const CpmPriorities = lazyPage(() => import("./pages/CpmPriorities"));
const CpmCheckin = lazyPage(() => import("./pages/CpmCheckin"));
const CpmTeamCheckins = lazyPage(() => import("./pages/CpmTeamCheckins"));
const TeamReviewsLanding = lazyPage(() => import("./pages/TeamReviewsLanding"));
const CpmEvaluations = lazyPage(() => import("./pages/CpmEvaluations"));
const CpmForcedRanking = lazyPage(() => import("./pages/CpmForcedRanking"));
const CpmReviewAdmin = lazyPage(() => import("./pages/CpmReviewAdmin"));
const CpmReports = lazyPage(() => import("./pages/CpmReports"));
const CpmPap = lazyPage(() => import("./pages/CpmPap"));
const CpmMyPap = lazyPage(() => import("./pages/CpmMyPap"));
const CpmTeamPaps = lazyPage(() => import("./pages/CpmTeamPaps"));
const MyDayMockup = lazyPage(() => import("./pages/peopleHr/MyDayMockup"));
const LeaveMockup = lazyPage(() => import("./pages/peopleHr/LeaveMockup"));
const TimesheetMockup = lazyPage(() => import("./pages/peopleHr/TimesheetMockup"));
const ApprovalsMockup = lazyPage(() => import("./pages/peopleHr/ApprovalsMockup"));
const ShiftsMockup = lazyPage(() => import("./pages/peopleHr/ShiftsMockup"));
const MyTimesheet = lazyPage(() => import("./pages/MyTimesheet"));
const ApproveTimesheets = lazyPage(() => import("./pages/ApproveTimesheets"));
const MyPTO = lazyPage(() => import("./pages/MyPTO"));
const PTOCalendar = lazyPage(() => import("./pages/PTOCalendar"));
const MyForecast = lazyPage(() => import("./pages/MyForecast"));
const ChargeCodes = lazyPage(() => import("./pages/ChargeCodes"));
const ActualsVsPlanned = lazyPage(() => import("./pages/ActualsVsPlanned"));
const HolidayCalendar = lazyPage(() => import("./pages/HolidayCalendar"));
const VoiceBiography = lazyPage(() => import("./pages/VoiceBiography"));
const ZohoLeaveSync = lazyPage(() => import("./pages/ZohoLeaveSync"));
const TaAdminBalances = lazyPage(() => import("./pages/TaAdminBalances"));
const TaAdminLeave = lazyPage(() => import("./pages/TaAdminLeave"));
const TaAdminCountryLeaves = lazyPage(() => import("./pages/TaAdminCountryLeaves"));
const TaAdminApprovals = lazyPage(() => import("./pages/TaAdminApprovals"));
const TaAdminAudit = lazyPage(() => import("./pages/TaAdminAudit"));
const TaAdminFollowUpEmails = lazyPage(() => import("./pages/TaAdminFollowUpEmails"));
const TaAdminApproverDelegations = lazyPage(() => import("./pages/TaAdminApproverDelegations"));
const TaAdminAttendance = lazyPage(() => import("./pages/TaAdminAttendance"));
const TaAdminTimesheets = lazyPage(() => import("./pages/TaAdminTimesheets"));
const TaAdminTimesheetControl = lazyPage(() => import("./pages/TaAdminTimesheetControl"));
const TaAdminReports = lazyPage(() => import("./pages/TaAdminReports"));
const TimekeepingAudit = lazyPage(() => import("./pages/TimekeepingAudit"));
const BulkTimesheetImport = lazyPage(() => import("./pages/BulkTimesheetImport"));
const PolicyBuilder = lazyPage(() => import("./pages/PolicyBuilder"));
const HandoverPlanForm = lazyPage(() => import("./pages/HandoverPlanForm"));
const ClerkEntryException = lazyPage(() => import("./pages/ClerkEntryException"));
const ReconciliationView = lazyPage(() => import("./pages/ReconciliationView"));
const CorpusAdmin = lazyPage(() => import("./pages/CorpusAdmin"));
const InboundApiKeys = lazyPage(() => import("./pages/InboundApiKeys"));
const InboundIntakeLog = lazyPage(() => import("./pages/InboundIntakeLog"));
const StaffingDashboard = lazyPage(() => import("./pages/StaffingDashboard"));
const StaffingLeadDetail = lazyPage(() => import("./pages/StaffingLeadDetail"));
const StaffingAdmin = lazyPage(() => import("./pages/StaffingAdmin"));
const AgencyIntelligenceDashboard = lazyPage(() => import("./pages/AgencyIntelligenceDashboard"));
const MorningBriefV2 = lazyPage(() => import("./pages/MorningBriefV2"));
const IntelV2ItemDetail = lazyPage(() => import("./pages/IntelV2ItemDetail"));
const IntelV2DailyAiReport = lazyPage(() => import("./pages/IntelV2DailyAiReport"));
const IntelV2WeeklyReport = lazyPage(() => import("./pages/IntelV2WeeklyReport"));
const WatchdogReportsList = lazyPage(() => import("./pages/WatchdogReportsList"));
const WatchdogReportDetail = lazyPage(() => import("./pages/WatchdogReportDetail"));
const IntelV2Audit = lazyPage(() => import("./pages/IntelV2Audit"));
const ConstellationGraph = lazyPage(() => import("./pages/ConstellationGraph"));
const IntelSpeakers = lazyPage(() => import("./pages/IntelSpeakers"));
const IntelEvents = lazyPage(() => import("./pages/IntelEvents"));
const IntelVideos = lazyPage(() => import("./pages/IntelVideos"));
const CuratedFeeds = lazyPage(() => import("./pages/CuratedFeeds"));
const MARadar = lazyPage(() => import("./pages/MARadar"));
const DiscoveryStream = lazyPage(() => import("./pages/DiscoveryStream"));
const DiscoveryDashboard = lazyPage(() => import("./pages/DiscoveryDashboard"));
const IntelCommandCenter = lazyPage(() => import("./pages/IntelCommandCenter"));
const IntelFusionCenter = lazyPage(() => import("./pages/IntelFusionCenter"));
const WeeklyIntelReports = lazyPage(() => import("./pages/WeeklyIntelReports"));
const ConvictionBoard = lazyPage(() => import("./pages/ConvictionBoard"));
const ConvictionThreadDetail = lazyPage(() => import("./pages/ConvictionThreadDetail"));
const TeamEducation = lazyPage(() => import("./pages/TeamEducation"));
const ContentDashboard = lazyPage(() => import("./pages/ContentDashboard"));
const DecisionBrief = lazyPage(() => import("./pages/DecisionBrief"));
const StandupPage = lazyPage(() => import("./pages/StandupPage"));
const ContentBrief = lazyPage(() => import("./pages/ContentBrief"));
const ContentDrafts = lazyPage(() => import("./pages/ContentDrafts"));
const ContentAnalytics = lazyPage(() => import("./pages/ContentAnalytics"));
const ContentSettings = lazyPage(() => import("./pages/ContentSettings"));
const ProcessList = lazyPage(() => import("./pages/ProcessList"));
const ProcessDetail = lazyPage(() => import("./pages/ProcessDetail"));
const DispositionBoard = lazyPage(() => import("./pages/DispositionBoard"));
const DiscoverySessions = lazyPage(() => import("./pages/DiscoverySessions"));
const ComplianceCrosswalk = lazyPage(() => import("./pages/ComplianceCrosswalk"));
const AgentSpecs = lazyPage(() => import("./pages/AgentSpecs"));
const AgentSpecDetail = lazyPage(() => import("./pages/AgentSpecDetail"));
const ScriptedActionsList = lazyPage(() => import("./pages/ScriptedActionsList"));
const TaskRegistry = lazyPage(() => import("./pages/TaskRegistry"));
const PolicyRules = lazyPage(() => import("./pages/PolicyRules"));
const AuditTrail = lazyPage(() => import("./pages/AuditTrail"));

function LegacyRedirect() {
  const { id } = useParams();
  return <Navigate to={`/leads/lead/${id}`} replace />;
}

// Backward compat: /dashboard/opportunity/:id -> /leads/lead/:id
function LegacyDashboardDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`/leads/lead/${id}`} replace />;
}

// Backward compat: /first-review/opportunity/:id -> /first-review/lead/:id
function LegacyFirstReviewDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`/first-review/lead/${id}`} replace />;
}

// Backward compat: /second-review/opportunity/:id -> /second-review/lead/:id
function LegacySecondReviewDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`/second-review/lead/${id}`} replace />;
}

// Backward compat: /lake/constellation/:opportunityId -> /intel/constellation/:opportunityId
function LegacyConstellationRedirect() {
  const { opportunityId } = useParams();
  return <Navigate to={`/intel/constellation/${opportunityId}`} replace />;
}

// Backward compat: /lake/agency/:agency -> /intel/agency/:agency
function LegacyAgencyRedirect() {
  const { agency } = useParams();
  return <Navigate to={`/intel/agency/${agency}`} replace />;
}

// OPS-ROUTES: /revenue/projects/:id -> /ops/projects/:id
function LegacyRevenueProjectRedirect() {
  const { id } = useParams();
  return <Navigate to={`/ops/projects/${id}`} replace />;
}

// OPS-ROUTES: /revenue/invoices/:id -> /ops/invoices/:id
function LegacyRevenueInvoiceRedirect() {
  const { id } = useParams();
  return <Navigate to={`/ops/invoices/${id}`} replace />;
}

// ── Frontend error telemetry ─────────────────────────────────────────
// Catches both module-init TDZ errors (window.onerror) and React render
// errors (AppErrorBoundary.componentDidCatch). Check browser console for
// "[ROS-ERR]" entries when the bug reproduces.
installErrorCollector();
patchChromeTranslateDom();
window.addEventListener('error', (evt) => {
  console.group('%c[ROS-ERR] Uncaught JS error', 'color:red;font-weight:bold;font-size:13px');
  console.error('message :', evt.message);
  console.error('source  :', evt.filename, `${evt.lineno}:${evt.colno}`);
  console.error('stack   :\n', evt.error?.stack ?? '(no stack)');
  console.groupEnd();
});

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { crashed: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error) {
    return { crashed: true, error };
  }

  componentDidCatch(error, info) {
    this.setState({ componentStack: info.componentStack });
    console.group('%c[ROS-ERR] React render crash', 'color:red;font-weight:bold;font-size:13px');
    console.error('message         :', error.message);
    console.error('stack           :\n', error.stack);
    console.error('component stack :', info.componentStack);
    console.groupEnd();
  }

  render() {
    if (this.state.crashed) {
      return (
        <div style={{ minHeight: '100vh', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
          <div style={{ maxWidth: 520 }}>
            <h2 style={{ color: '#991b1b', fontWeight: 'bold', fontSize: 16, marginBottom: 8 }}>Application Error</h2>
            <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12, fontFamily: 'monospace' }}>{this.state.error?.message}</p>
            <p style={{ color: '#6b7280', fontSize: 12, marginBottom: 4 }}>Component stack (copy this for debugging):</p>
            <pre style={{ fontSize: 11, color: '#374151', background: 'white', border: '1px solid #e5e7eb', borderRadius: 6, padding: 12, overflowX: 'auto', maxHeight: 240, marginBottom: 16 }}>
              {this.state.componentStack}
            </pre>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => window.location.reload()}
              style={{ padding: '8px 20px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}
            >
              Reload page
            </button>
            <IncidentCrashBar error={this.state.error} componentStack={this.state.componentStack} />
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ProtectedRoute() {
  const { user, loading, isPending } = useAuth();

  if (loading) {
    return <RLoaderScreen label="Loading" variant="trace" bg="navy" speed={0.3} size={100} />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (isPending) {
    return <Navigate to="/pending" replace />;
  }

  return <Layout />;
}

// Route-level permission guard: blocks rendering a page when the user lacks the
// required permission — even via direct URL — and redirects home. Complements the
// sidebar gating (which only hides the menu item). Uses the same permission set
// the backend sends, so it matches enforcement exactly.
function RequirePermission({ perm, children }) {
  const { hasPermission } = useAuth();
  if (!hasPermission(perm)) {
    return <Navigate to="/home" replace />;
  }
  return children;
}

function RequireAnyPermission({ perms, children }) {
  const { hasPermission } = useAuth();
  if (!perms.some((p) => hasPermission(p))) {
    return <Navigate to="/home" replace />;
  }
  return children;
}

// Rewrite all fetch("/api/...") calls to use the base path (e.g. /app1/api/...)
// and, for those API calls, track backend availability: a 5xx gateway error or a
// network failure (almost always a deploy in progress) raises the app-wide
// backend-down flag; any clean response clears it so recovery is automatic.
const _basePath = import.meta.env.BASE_URL.replace(/\/$/, ""); // "/app1"
const _origFetch = window.fetch.bind(window);
window.fetch = (url, ...args) => {
  const isApi = typeof url === "string" && url.startsWith("/api/");
  const target = isApi ? _basePath + url : url;
  if (!isApi) {
    return _origFetch(target, ...args);
  }
  const method = (args[0] && args[0].method) || "GET";
  return _origFetch(target, ...args).then(
    (res) => {
      // Helpdesk proxies ITMS — its 502/503 means ITMS is down, not ROS.
      // Do not blank the whole app. Bug reporting is manual only (no auto-queue).
      if (!isHelpdeskApiUrl(url)) {
        setBackendDown(res.status === 502 || res.status === 503 || res.status === 504);
      }
      recordApiCall(url, res, method);
      return res;
    },
    (err) => {
      // Ignore caller-initiated aborts; any other network error means unreachable.
      if (err?.name !== "AbortError") {
        if (!isHelpdeskApiUrl(url)) {
          setBackendDown(true);
        }
        recordApiCall(url, { status: 0 }, method);
      }
      throw err;
    }
  );
};

// Docker/hub builds use FRONTEND_BASE_PATH=/app1/. Opening localhost:4001/ or
// /people/leave without that prefix makes <Router basename="/app1"> render
// nothing (blank white screen). Bounce into the basename before mount.
if (
  _basePath &&
  _basePath !== "/" &&
  !(
    window.location.pathname === _basePath ||
    window.location.pathname.startsWith(`${_basePath}/`)
  )
) {
  const suffix =
    window.location.pathname === "/" ? "/" : window.location.pathname;
  window.location.replace(
    `${_basePath}${suffix}${window.location.search}${window.location.hash}`
  );
} else {
ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <BrowserRouter basename={_basePath}>
      <ThemeProvider>
      <AuthProvider>
        <AppErrorBoundary>
        <BackendDownOverlay />
        <Suspense fallback={<RLoaderScreen label="Loading" variant="trace" bg="navy" speed={0.3} size={100} />}>
        <Routes>
          {/* Public route */}
          <Route path="/login" element={<Login />} />

          {/* Pending approval route — authenticated but no role assigned */}
          <Route path="/pending" element={<PendingApproval />} />

          {/* Protected routes */}
          <Route element={<ProtectedRoute />}>
            {/* Root: send the user to wherever they were last (tracked
                by useLastVisitedTracker in Layout), or /home for new sessions */}
            <Route path="/" element={<Navigate to="/home" replace />} />
            {/* User home */}
            <Route path="/home" element={<UserDashboard />} />

            {/* Time & Attendance — gated by timekeeping.module (Admin-only until wider grant) */}
            <Route path="/people/voice-biography" element={<RequirePermission perm="people.biography.edit"><VoiceBiography /></RequirePermission>} />
            <Route path="/people/my-day" element={<RequirePermission perm="timekeeping.module"><MyDayMockup /></RequirePermission>} />
            <Route path="/people/shifts" element={<RequirePermission perm="timekeeping.module"><ShiftsMockup /></RequirePermission>} />
            <Route path="/people/leave" element={<RequirePermission perm="timekeeping.module"><LeaveMockup /></RequirePermission>} />
            <Route path="/people/time" element={<RequirePermission perm="timekeeping.module"><TimesheetMockup /></RequirePermission>} />
            <Route path="/people/approvals" element={<RequireAnyPermission perms={["timekeeping.approve", "pto.approve", "timekeeping.admin", "admin.settings"]}><ApprovalsMockup /></RequireAnyPermission>} />

            {/* Primary routes */}
            <Route path="/leads" element={<LeadTriageLayout><Dashboard /></LeadTriageLayout>} />
            <Route path="/leads/attachments" element={<LeadTriageLayout><AttachmentsAudit /></LeadTriageLayout>} />
            <Route path="/leads/trust" element={<LeadTriageLayout><LeadTrustAudit /></LeadTriageLayout>} />
            <Route path="/leads/lead/:id" element={<OpportunityDetail />} />
            {/* TRIAGE-UNIFY: Unified Command Center */}
            <Route path="/triage" element={<RequirePermission perm="leads.manual_screen_1"><LeadTriageLayout><TriageCenter /></LeadTriageLayout></RequirePermission>} />
            {/* Legacy screening/triage/command redirects → /triage */}
            <Route path="/screening" element={<Navigate to="/triage" replace />} />
            <Route path="/screening/lead/:id" element={<OpportunityDetail />} />
            <Route path="/first-review" element={<Navigate to="/triage" replace />} />
            <Route path="/first-review/lead/:id" element={<OpportunityDetail />} />
            <Route path="/second-review" element={<Navigate to="/triage" replace />} />
            <Route path="/second-review/lead/:id" element={<OpportunityDetail />} />
            <Route path="/pipeline" element={<UnifiedPipeline />} />
            <Route path="/opportunities" element={<Navigate to="/pipeline" replace />} />
            <Route path="/opportunities/opportunity/:id" element={<CrmOpportunityDetail />} />
            <Route path="/upload" element={<Upload />} />
            <Route path="/sam" element={<SamSearch />} />
            <Route path="/sam/bulk" element={<SamBulkIngestion />} />
            <Route path="/highergov/bulk" element={<RequirePermission perm="admin.data"><HigherGovBulkIngestion /></RequirePermission>} />
            {/* CONSOLIDATION: /settings is no longer a distinct page; admin
                settings live in Platform Control (/settings/platform). The
                redirect keeps existing bookmarks working. The Settings.jsx
                file is still imported but unreachable from nav — slated
                for deletion in a follow-up cleanup pass. */}
            <Route path="/settings" element={<Navigate to="/settings/platform" replace />} />
            <Route path="/settings/platform" element={<PlatformControl />} />
            <Route path="/settings/evolution" element={<PromptEvolution />} />
            <Route path="/settings/users" element={<RequirePermission perm="admin.users"><Users /></RequirePermission>} />
            <Route path="/settings/roles" element={<RequirePermission perm="admin.roles"><RolesAdmin /></RequirePermission>} />
            <Route path="/settings/data" element={<DataAdmin />} />
            <Route path="/settings/corpus" element={<CorpusAdmin />} />
            <Route path="/settings/api-keys" element={<RequirePermission perm="admin.api_keys"><InboundApiKeys /></RequirePermission>} />
            <Route path="/settings/inbound-log" element={<InboundIntakeLog />} />
            <Route path="/settings/notifications" element={<NotificationSettings />} />
            <Route path="/settings/services" element={<ServicesAdmin />} />
            <Route path="/activity" element={<ActivityLog />} />
            <Route path="/activity/usage" element={<RequirePermission perm="admin.settings"><UsageBoard /></RequirePermission>} />
            <Route path="/ops/system" element={<OperationsDashboard />} />
            <Route path="/admin/system-log" element={<SystemLog />} />
            <Route path="/admin/task-monitor" element={<TaskMonitor />} />
            <Route path="/admin/focus-compliance" element={<RequirePermission perm="rro.admin"><FocusCompliance /></RequirePermission>} />
            <Route path="/proactive" element={<ProactiveLeads />} />
            <Route path="/proactive/lead/:id" element={<ProactiveLeadDetail />} />
            <Route path="/help" element={<Help />} />
            <Route path="/about" element={<About />} />
            <Route path="/data-admin" element={<DataAdmin />} />
            <Route path="/users" element={<Users />} />
            <Route path="/admin/roles" element={<RolesAdmin />} />
            <Route path="/profile" element={<UserProfile />} />
            <Route path="/users/:id/profile" element={<UserProfile />} />

            {/* Ingestion routes */}
            <Route path="/ingestion/run/:runId" element={<BulkIngestionResults />} />

            {/* CRM routes — /crm/pulse and /crm/pipeline redirect to unified /pipeline */}
            <Route path="/crm/pulse" element={<Navigate to="/pipeline" replace />} />
            <Route path="/crm/pipeline" element={<Navigate to="/pipeline" replace />} />
            <Route path="/crm/opportunities" element={<CrmOpportunities />} />
            <Route path="/crm/opportunity/:id" element={<CrmOpportunityDetail />} />
            <Route path="/crm/contacts" element={<CrmContacts />} />
            <Route path="/crm/exceptions" element={<CrmExceptions />} />
            <Route path="/crm/contact/:id" element={<CrmContactDetail />} />
            <Route path="/crm/organizations" element={<CrmOrganizations />} />
            <Route path="/crm/organization/:id" element={<CrmOrganizationDetail />} />
            <Route path="/crm/tasks" element={<CrmTasks />} />
            <Route path="/crm/import" element={<CrmImport />} />

            {/* Past Performance routes */}
            <Route path="/past-performance" element={<PastPerformance />} />
            <Route path="/past-performance/new" element={<PastPerformanceDetail />} />
            <Route path="/past-performance/:id" element={<PastPerformanceDetail />} />
            {/* MKTRESP-1: Marketing Responses — Board/Stats/Routing wrapped in tab layout */}
            <Route path="/growth/marketing-responses/:id" element={<MarketingResponseDetail />} />
            <Route path="/growth/marketing-responses" element={<MarketingResponsesLayout />}>
              <Route index element={<MarketingResponsesBoard />} />
              <Route path="stats" element={<MarketingResponsesStats />} />
              <Route path="routing" element={<MarketingResponsesRoutingAdmin />} />
            </Route>
            {/* RD-RESPONSES-1: R&D Response Workflow */}
            <Route path="/growth/rd-responses/new" element={<RdResponseEnroll />} />
            <Route path="/growth/rd-responses/:id" element={<RdResponseDetail />} />
            <Route path="/growth/rd-responses" element={<RdResponseBoard />} />
            <Route path="/growth/unsolicited-outreach" element={<UnsolicitedOutreachBoard />} />
            {/* FEEDBACK-1: Team Feedback System */}
            <Route path="/feedback" element={<MyFeedbackSubmissions />} />
            <Route path="/feedback/:id" element={<FeedbackDetail />} />
            <Route path="/settings/feedback" element={<FeedbackAdmin />} />
            <Route path="/helpdesk" element={<RequirePermission perm="helpdesk.view"><Helpdesk /></RequirePermission>} />
            <Route path="/helpdesk/:id" element={<RequirePermission perm="helpdesk.view"><HelpdeskDetail /></RequirePermission>} />
            {/* INTEL-V2-PRODUCTS — 6 tabs inside a shared V2 Fusion Intel shell */}
            <Route path="/intel/v2" element={<IntelV2Layout />}>
              <Route index element={<IntelV2Feed />} />
              <Route path="implications" element={<IntelV2Implications />} />
              <Route path="decisions" element={<IntelV2Decisions />} />
              <Route path="alerts" element={<IntelV2BreakingAlerts />} />
              <Route path="assessments" element={<IntelV2Assessments />} />
              <Route path="fusion" element={<IntelV2Fusion />} />
            </Route>

            {/* AI Hub routes */}
            <Route path="/hub" element={<HubPage />} />
            <Route path="/hub/transcript-tool" element={<TranscriptTool />} />
            <Route path="/hub/weekly-focus" element={<HubPage />} />
            <Route path="/hub/weekly-focus/team" element={<HubTeamNotes />} />
            <Route path="/hub/templates" element={<HubTemplates />} />
            <Route path="/hub/documents" element={<HubDocuments />} />

            {/* People module routes */}
            <Route path="/people" element={<RequirePermission perm="people.view"><PersonnelList /></RequirePermission>} />
            <Route path="/people/certifications" element={<RequirePermission perm="people.view"><PersonnelCertifications /></RequirePermission>} />
            <Route path="/people/rro" element={<RequirePermission perm="people.view"><RROView /></RequirePermission>} />
            <Route path="/people/rro/:personnelId" element={<RequirePermission perm="rro.view"><RROView /></RequirePermission>} />
            <Route path="/people/actionables" element={<ActionablesView />} />
            <Route path="/people/aars" element={<AARList />} />
            <Route path="/people/aars/:id" element={<AARDetail />} />
            {/* VOICE-2: Journal & To-Do routes */}
            <Route path="/people/journal" element={<Journal />} />
            <Route path="/people/todos" element={<MyTodos />} />
            <Route path="/notepad" element={<PeopleNotepad />} />

            {/* CPM routes (RRO-CPM-1) */}
            <Route path="/people/priorities" element={<RequirePermission perm="people.view"><CpmPriorities /></RequirePermission>} />
            <Route path="/people/checkin" element={<RequirePermission perm="rro.view"><CpmCheckin /></RequirePermission>} />
            <Route path="/people/self-assessment" element={<Navigate to="/people/checkin?step=self-assessment" replace />} />
            <Route path="/people/team-reviews" element={<RequirePermission perm="rro.manage_team"><TeamReviewsLanding /></RequirePermission>} />
            <Route path="/people/team-checkins" element={<RequirePermission perm="rro.manage_team"><CpmTeamCheckins /></RequirePermission>} />
            <Route path="/people/evaluations" element={<RequirePermission perm="rro.manage_team"><CpmEvaluations /></RequirePermission>} />
            <Route path="/people/evaluations/ranking" element={<RequirePermission perm="rro.manage_team"><CpmForcedRanking /></RequirePermission>} />
            <Route path="/people/review-admin" element={<RequirePermission perm="rro.admin"><CpmReviewAdmin /></RequirePermission>} />
            <Route path="/people/reports" element={<RequirePermission perm="rro.admin"><CpmReports /></RequirePermission>} />
            <Route path="/people/my-pap" element={<RequirePermission perm="rro.view"><CpmMyPap /></RequirePermission>} />
            <Route path="/people/team-paps" element={<RequirePermission perm="rro.manage_team"><CpmTeamPaps /></RequirePermission>} />

            <Route path="/people/pap/:papId" element={<RequirePermission perm="rro.view"><CpmPap /></RequirePermission>} />

            {/* Timekeeping routes (TIMESHEET-1) — dark except Holidays until timekeeping.module granted */}
            <Route path="/people/timesheet" element={<RequirePermission perm="timekeeping.module"><MyTimesheet /></RequirePermission>} />
            <Route path="/people/timesheet/approve" element={<RequireAnyPermission perms={["timekeeping.approve", "timekeeping.admin", "admin.settings"]}><ApproveTimesheets /></RequireAnyPermission>} />
            <Route path="/people/timesheet/pto" element={<RequirePermission perm="timekeeping.module"><MyPTO /></RequirePermission>} />
            <Route path="/people/timesheet/pto-calendar" element={<RequirePermission perm="timekeeping.module"><PTOCalendar /></RequirePermission>} />
            <Route path="/people/timesheet/forecast" element={<RequirePermission perm="timekeeping.module"><MyForecast /></RequirePermission>} />
            <Route path="/people/timesheet/charge-codes" element={<RequireAnyPermission perms={["timekeeping.admin", "admin.settings", "pto.admin"]}><TaAdminShell hub="time"><ChargeCodes /></TaAdminShell></RequireAnyPermission>} />
            <Route path="/people/timesheet/compliance" element={<Navigate to="/people/timesheet/admin/control?panel=reports" replace />} />
            <Route path="/people/timesheet/actuals" element={<RequirePermission perm="timekeeping.module"><ActualsVsPlanned /></RequirePermission>} />
            <Route path="/people/timesheet/holidays" element={<RequireAnyPermission perms={TA_SETUP_ROUTE_PERMS}><TaAdminShell hub="setup"><HolidayCalendar /></TaAdminShell></RequireAnyPermission>} />
            <Route path="/people/timesheet/zoho-leave" element={<RequireAnyPermission perms={TA_SETUP_ROUTE_PERMS}><TaAdminShell hub="setup"><ZohoLeaveSync /></TaAdminShell></RequireAnyPermission>} />
            <Route path="/people/timesheet/admin/balances" element={<RequireAnyPermission perms={["timekeeping.admin", "admin.settings", "pto.admin"]}><TaAdminShell hub="leave"><TaAdminBalances /></TaAdminShell></RequireAnyPermission>} />
            <Route path="/people/timesheet/admin/attendance" element={<RequireAnyPermission perms={["timekeeping.attendance.admin", "timekeeping.attendance.download", "timekeeping.admin", "admin.settings", "pto.admin"]}><TaAdminAttendance /></RequireAnyPermission>} />
            <Route path="/people/timesheet/admin/approvals" element={<RequireAnyPermission perms={["timekeeping.admin", "admin.settings", "pto.admin"]}><TaAdminApprovals /></RequireAnyPermission>} />
            <Route path="/people/timesheet/admin/leave" element={<RequireAnyPermission perms={["timekeeping.admin", "admin.settings", "pto.admin"]}><TaAdminShell hub="leave"><TaAdminLeave /></TaAdminShell></RequireAnyPermission>} />
            <Route path="/people/timesheet/admin/timesheets" element={<RequireAnyPermission perms={["timekeeping.admin", "admin.settings"]}><TaAdminShell hub="time"><TaAdminTimesheets /></TaAdminShell></RequireAnyPermission>} />
            <Route path="/people/timesheet/admin/control" element={<RequireAnyPermission perms={["timekeeping.admin", "admin.settings"]}><TaAdminShell hub="time"><TaAdminTimesheetControl /></TaAdminShell></RequireAnyPermission>} />
            <Route path="/people/timesheet/admin/reports" element={<RequireAnyPermission perms={TA_REPORTS_ROUTE_PERMS}><TaAdminShell hub="time"><TaAdminReports /></TaAdminShell></RequireAnyPermission>} />
            <Route path="/people/timesheet/admin/audit" element={<RequireAnyPermission perms={[...TA_SETUP_ROUTE_PERMS, "timekeeping.reports"]}><TaAdminShell hub="setup"><TaAdminAudit /></TaAdminShell></RequireAnyPermission>} />
            <Route path="/people/timesheet/admin/follow-up-emails" element={<RequireAnyPermission perms={TA_SETUP_ROUTE_PERMS}><TaAdminShell hub="time"><TaAdminFollowUpEmails /></TaAdminShell></RequireAnyPermission>} />
            <Route path="/people/timesheet/admin/approver-delegations" element={<RequireAnyPermission perms={TA_SETUP_ROUTE_PERMS}><TaAdminShell hub="setup"><TaAdminApproverDelegations /></TaAdminShell></RequireAnyPermission>} />
            <Route path="/people/timesheet/audit" element={<Navigate to="/people/timesheet/admin/audit" replace />} />
            <Route path="/people/timesheet/bulk-import" element={<RequirePermission perm="timekeeping.module"><BulkTimesheetImport /></RequirePermission>} />
            {/* TIMESHEET-1A: PTO Enhancement Patch */}
            <Route path="/people/timesheet/policies" element={<RequireAnyPermission perms={[...TA_SETUP_ROUTE_PERMS, "pto.policy.manage"]}><TaAdminShell hub="setup"><PolicyBuilder /></TaAdminShell></RequireAnyPermission>} />
            <Route path="/people/timesheet/admin/country-leaves" element={<RequireAnyPermission perms={[...TA_SETUP_ROUTE_PERMS, "pto.policy.manage"]}><TaAdminShell hub="setup"><TaAdminCountryLeaves /></TaAdminShell></RequireAnyPermission>} />
            <Route path="/people/timesheet/handover" element={<RequirePermission perm="timekeeping.module"><HandoverPlanForm /></RequirePermission>} />
            <Route path="/people/timesheet/clerk-entry" element={<RequireAnyPermission perms={["timekeeping.admin", "admin.settings", "timekeeping.clerk_entry.initiate"]}><TaAdminShell hub="time"><ClerkEntryException /></TaAdminShell></RequireAnyPermission>} />
            <Route path="/people/timesheet/reconciliation" element={<RequireAnyPermission perms={["timekeeping.admin", "admin.settings", "timekeeping.reconciliation.manage"]}><TaAdminShell hub="time"><ReconciliationView /></TaAdminShell></RequireAnyPermission>} />

            {/* Staffing routes (STAFFING-1 / STAFFING-3) */}
            <Route path="/staffing" element={<StaffingDashboard />} />
            <Route path="/staffing/lead/:id" element={<StaffingLeadDetail />} />
            <Route path="/staffing/admin" element={<StaffingAdmin />} />

            {/* OSINT Lake / Intelligence routes (renamed /lake/ → /intel/) */}
            <Route path="/intel" element={<IntelFusionCenter />} />
            <Route path="/intel/decisions" element={<DecisionBrief />} />
            <Route path="/intel/convictions" element={<IntelStreamLayout><ConvictionBoard /></IntelStreamLayout>} />
            <Route path="/intel/standup" element={<StandupPage />} />
            <Route path="/intel/convictions/:id" element={<ConvictionThreadDetail />} />
            <Route path="/intel/education" element={<TeamEducation />} />
            <Route path="/intel/stream" element={<IntelStreamLayout><LakeStream /></IntelStreamLayout>} />
            <Route path="/intel/weekly-reports" element={<WeeklyIntelReports />} />
            <Route path="/intel/weekly-reports/:topicKey/archive" element={<WeeklyIntelReports />} />
            <Route path="/intel/weekly-reports/:topicKey/:weekStart" element={<WeeklyIntelReports />} />
            <Route path="/intel/brief" element={<Navigate to="/intel/brief-v2" replace />} />
            <Route path="/intel/brief/archive" element={<LakeBrief />} />
            <Route path="/intel/brief-v2" element={<MorningBriefV2 />} />
            <Route path="/intel/morning-brief-v2" element={<Navigate to="/intel/brief-v2" replace />} />
            <Route path="/intel/v2/item/:id" element={<IntelV2ItemDetail />} />
            <Route path="/intel/daily-frontier-ai" element={<IntelV2DailyAiReport />} />
            <Route path="/intel/frontier-v2" element={<Navigate to="/intel/daily-frontier-ai" replace />} />
            <Route path="/intel/daily-ai-v2" element={<Navigate to="/intel/daily-frontier-ai" replace />} />
            <Route path="/intel/weekly-v2" element={<IntelV2WeeklyReport />} />
            <Route path="/intel/weekly-v2/:topicKey/:weekStart" element={<IntelV2WeeklyReport />} />
            <Route path="/intel/watchdog" element={<RequirePermission perm="leads.view"><WatchdogReportsList /></RequirePermission>} />
            <Route path="/intel/watchdog/:reportId" element={<RequirePermission perm="leads.view"><WatchdogReportDetail /></RequirePermission>} />
            <Route path="/intel/v2/audit" element={<IntelV2Audit />} />
            <Route path="/intel/v2/briefs" element={<Navigate to="/intel/daily-frontier-ai?tab=archive" replace />} />
            <Route path="/intel/frontier" element={<Navigate to="/intel/daily-frontier-ai" replace />} />
            <Route path="/intel/frontier/:briefId" element={<Navigate to="/intel/daily-frontier-ai" replace />} />
            <Route path="/intel/constellation/:opportunityId" element={<ConstellationGraph />} />
            <Route path="/intel/key-talent" element={<Navigate to="/staffing" replace />} />
            <Route path="/intel/search" element={<LakeSearch />} />
            <Route path="/intel/actions" element={<LakeActions />} />
            <Route path="/intel/admin" element={<LakeAdmin />} />
            <Route path="/intel/dashboard" element={<RequirePermission perm="intel.view"><IntelDashboard /></RequirePermission>} />
            <Route path="/intel/agency/:agency" element={<AgencyIntelligenceDashboard />} />
            <Route path="/intel/speakers" element={<IntelSpeakers />} />
            <Route path="/intel/events" element={<IntelStreamLayout><IntelEvents /></IntelStreamLayout>} />
            <Route path="/intel/videos" element={<IntelStreamLayout><IntelVideos /></IntelStreamLayout>} />
            <Route path="/intel/feeds" element={<CuratedFeeds />} />
            <Route path="/intel/ma-radar" element={<MARadar />} />
            {/* DISCOVERY-1: Discovery Scout Agent */}
            <Route path="/intel/discovery" element={<DiscoveryStream />} />
            <Route path="/intel/discovery/admin" element={<DiscoveryDashboard />} />

            {/* Legacy /lake/ redirects (INTEL-ROS-ALIGNMENT-PATCH) */}
            <Route path="/lake/stream" element={<Navigate to="/intel/stream" replace />} />
            <Route path="/lake/brief" element={<Navigate to="/intel/brief" replace />} />
            <Route path="/lake/brief/archive" element={<Navigate to="/intel/brief/archive" replace />} />
            <Route path="/lake/constellation/:opportunityId" element={<LegacyConstellationRedirect />} />
            <Route path="/lake/key-talent" element={<Navigate to="/staffing" replace />} />
            <Route path="/lake/search" element={<Navigate to="/intel/search" replace />} />
            <Route path="/lake/actions" element={<Navigate to="/intel/actions" replace />} />
            <Route path="/lake/admin" element={<Navigate to="/intel/admin" replace />} />
            <Route path="/lake/agency/:agency" element={<LegacyAgencyRedirect />} />

            {/* Agent routes — /new and /templates BEFORE /:slug to prevent slug capture */}
            <Route path="/agents" element={<AgentRegistry />} />
            <Route path="/agents/new" element={<AgentBuilder />} />
            <Route path="/agents/templates" element={<AgentTemplates />} />
            <Route path="/agents/runs" element={<AgentRuns />} />
            <Route path="/agents/:slug" element={<AgentDetail />} />

            {/* Contract Vehicle routes */}
            <Route path="/contract-vehicles" element={<RequirePermission perm="contract_vehicles.view"><ContractVehicles /></RequirePermission>} />
            <Route path="/contract-vehicles/:slug" element={<RequirePermission perm="contract_vehicles.view"><ContractVehicleDetail /></RequirePermission>} />

            {/* Builder routes (Addendum 2) */}
            <Route path="/builder/processes" element={<ProcessList />} />
            <Route path="/builder/processes/:id" element={<ProcessDetail />} />
            <Route path="/builder/disposition" element={<DispositionBoard />} />
            <Route path="/builder/discovery" element={<DiscoverySessions />} />
            <Route path="/builder/compliance" element={<ComplianceCrosswalk />} />
            <Route path="/builder/specs" element={<AgentSpecs />} />
            <Route path="/builder/specs/:key" element={<AgentSpecDetail />} />
            <Route path="/builder/actions" element={<ScriptedActionsList />} />
            <Route path="/builder/tasks" element={<TaskRegistry />} />
            <Route path="/builder/policy" element={<RequirePermission perm="admin.settings"><PolicyRules /></RequirePermission>} />
            <Route path="/builder/audit" element={<AuditTrail />} />

            {/* Content Engine routes (CONTENT-FE-1) */}
            <Route path="/content" element={<ContentDashboard />} />
            <Route path="/content/brief" element={<ContentBrief />} />
            <Route path="/content/drafts" element={<ContentDrafts />} />
            <Route path="/content/analytics" element={<ContentAnalytics />} />
            <Route path="/content/settings" element={<ContentSettings />} />

            {/* Operations Module routes (OPS-1 + OPS-1B + OPS-ROUTES) */}
            <Route path="/ops" element={<Navigate to="/ops/dashboard" replace />} />
            <Route path="/ops/dashboard" element={<OpsDashboard />} />
            <Route path="/ops/projects" element={<RevenueProjects />} />
            <Route path="/ops/projects/:id" element={<OpsProjectDetail />} />
            <Route path="/ops/workforce" element={<WorkforcePlanning />} />
            <Route path="/ops/profitability" element={<OpsProfitability />} />
            <Route path="/ops/forecast" element={<RevenueForecast />} />
            <Route path="/ops/allocation" element={<RevenueAllocation />} />
            <Route path="/ops/invoices" element={<RequirePermission perm="revenue.billing"><RevenueInvoices /></RequirePermission>} />
            <Route path="/ops/invoices/:id" element={<RevenueInvoiceDetail />} />
            <Route path="/ops/schedule" element={<RevenueSchedule />} />

            {/* ISO Module routes (F0-2) — placeholder, real pages ship per ticket */}
            <Route path="/iso" element={<Navigate to="/iso/documents" replace />} />
            <Route path="/iso/documents" element={<RequireAnyPermission perms={["iso.view", "iso.documents.view", "iso.requests.create"]}><DocumentMatrix /></RequireAnyPermission>} />
            <Route path="/iso/non-conformities" element={<RequirePermission perm="iso.view"><NonConformities /></RequirePermission>} />
            <Route path="/iso/risks" element={<RequirePermission perm="iso.view"><RiskMatrix /></RequirePermission>} />
            <Route path="/iso/satisfaction" element={<RequirePermission perm="iso.view"><Satisfaction /></RequirePermission>} />
            <Route path="/iso/action-plans" element={<RequirePermission perm="iso.view"><ActionPlans /></RequirePermission>} />
            {/* Document Requests page no longer used:
            <Route path="/iso/requests" element={<RequireAnyPermission perms={["iso.view", "iso.requests.create"]}><DocumentRequests /></RequireAnyPermission>} />
            */}
            <Route path="/iso/admin" element={<RequirePermission perm="iso.manage"><IsoAdminPanel /></RequirePermission>} />
            <Route path="/iso/my-panel" element={<RequirePermission perm="iso.view"><MyPanelIso /></RequirePermission>} />
            <Route path="/iso/access-control" element={<RequirePermission perm="iso.admin"><IsoRiskAccessControl /></RequirePermission>} />
            <Route path="/iso/search" element={<RequireAnyPermission perms={["iso.view", "iso.documents.view"]}><SearchCenter /></RequireAnyPermission>} />
            <Route path="/iso/coding-wizard" element={<RequirePermission perm="iso.edit"><CodingWizard /></RequirePermission>} />
            <Route path="/iso/control-center" element={<RequirePermission perm="iso.view"><ControlCenter /></RequirePermission>} />
            <Route path="/iso/objectives-kpis" element={<RequirePermission perm="iso.view"><ObjectivesKpis /></RequirePermission>} />
            <Route path="/iso/clauses" element={<RequirePermission perm="iso.manage"><ClausesAdmin /></RequirePermission>} />

            {/* Legacy /revenue/ redirects → /ops/ (OPS-ROUTES) */}
            <Route path="/revenue" element={<Navigate to="/ops/dashboard" replace />} />
            <Route path="/revenue/projects" element={<Navigate to="/ops/projects" replace />} />
            <Route path="/revenue/projects/:id" element={<LegacyRevenueProjectRedirect />} />
            <Route path="/revenue/forecast" element={<Navigate to="/ops/forecast" replace />} />
            <Route path="/revenue/allocation" element={<Navigate to="/ops/allocation" replace />} />
            <Route path="/revenue/invoices" element={<Navigate to="/ops/invoices" replace />} />
            <Route path="/revenue/invoices/:id" element={<LegacyRevenueInvoiceRedirect />} />
            <Route path="/revenue/schedule" element={<Navigate to="/ops/schedule" replace />} />

            {/* Workflow routes (WORKFLOW-1) */}
            <Route path="/workflows/tasks" element={<WorkflowMyTasks />} />
            <Route path="/workflows/templates" element={<WorkflowTemplates />} />
            <Route path="/workflows/templates/:id" element={<WorkflowTemplateDetail />} />
            <Route path="/workflows/runs" element={<WorkflowRuns />} />
            <Route path="/workflows/runs/:id" element={<WorkflowRunDetail />} />
            <Route path="/workflows/schedules" element={<WorkflowSchedules />} />
            <Route path="/workflows/reports" element={<WorkflowReports />} />
            <Route path="/workflows/agents" element={<WorkflowAgentPerformance />} />
            <Route path="/workflows/ci-ideas" element={<CIIdeasBacklog />} />

            {/* Forecast routes */}
            <Route path="/forecast" element={<ForecastDashboard />} />

            {/* Coordination routes — lead-triage/command → unified /triage */}
            <Route path="/lead-triage" element={<Navigate to="/triage" replace />} />
            <Route path="/coord/command" element={<Navigate to="/triage" replace />} />
            <Route path="/coord/tasks" element={<CoordMyTasks />} />
            <Route path="/coord/impressions" element={<CoordImpressions />} />
            <Route path="/coord/accountability" element={<CoordAccountability />} />
            {/* Legacy redirects: calibration & platform moved under /settings */}
            <Route path="/coord/calibration" element={<Navigate to="/settings/calibration" replace />} />
            <Route path="/coord/platform" element={<Navigate to="/settings/platform" replace />} />

            {/* Backward compatibility redirects */}
            <Route path="/finance" element={<Navigate to="/ops/dashboard" replace />} />
            <Route path="/dashboard" element={<Navigate to="/leads" replace />} />
            <Route path="/dashboard/opportunity/:id" element={<LegacyDashboardDetailRedirect />} />
            <Route path="/first-review/opportunity/:id" element={<LegacyFirstReviewDetailRedirect />} />
            <Route path="/second-review/opportunity/:id" element={<LegacySecondReviewDetailRedirect />} />
            <Route path="/opportunity/:id" element={<LegacyRedirect />} />
          </Route>
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
        </Suspense>
        </AppErrorBoundary>
      </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  </React.StrictMode>
);
}
